import videojs, { VideoJsPlayerOptions } from 'video.js';
import { IPlayerDataAsset } from 'src/interfaces/PlayerData';
import { IDRMSource, IVideoJsOptions, IVideoJSPlayer } from 'src/interfaces/VideoJS';
import { isUrlFromCloudFront } from 'src/utils/urlUtils';
import DRMService from 'src/services/DRMService';
import { DRM_PROVIDERS, EXTENSION_TYPES, TRANSMISSION_TYPE } from 'src/interfaces/enums';
import { signCloudfrontUrl } from 'src/utils/signatureUtils';
import { IApplicationData } from 'src/interfaces/ApplicationData';
import { DEFAULT_PLAYER_OPTIONS } from '../shared/constants';
import MuxService from '../MuxService';
import { pallyconLicenseEndpoint } from '../DRMService/constants';

class PlayerConfigService {
  public config: IVideoJsOptions;

  isWithCredentials(asset: IPlayerDataAsset, live?: boolean) {
    if (live) return false;
    return isUrlFromCloudFront(asset.url);
  }

  async parsePlayerData(applicationData: IApplicationData): Promise<VideoJsPlayerOptions> {
    const { transmissionType, mediaAssets } = applicationData;
    const isLive = transmissionType === TRANSMISSION_TYPE.live;

    this.config = DEFAULT_PLAYER_OPTIONS();

    if (mediaAssets[0]) {
      this.config.html5.vhs.withCredentials = this.isWithCredentials(mediaAssets[0], isLive);
    }

    this.config.sources = await this.extractSources(applicationData);
    this.config.plugins = this.getPlugins(applicationData);
    this.config.html5.vhs.isEncryptedUrl = this.isEncryptedUrl(applicationData);

    return this.config;
  }

  async extractSources(applicationData: IApplicationData): Promise<videojs.Tech.SourceObject[]> {
    const { mediaAssets, isDrmEnabled } = applicationData;

    const sources = (mediaAssets || []).map((asset) => ({
      src: this.getSource(applicationData, asset),
      type: asset.contentType,
      withCredentials: this.isWithCredentials(asset),
      objectKey: asset.objectKey || '',
    }));

    if (isDrmEnabled) return [await this.getDRMSource(sources, applicationData)];

    return sources.slice(0, 1);
  }

  getSource(applicationData: IApplicationData, asset: IPlayerDataAsset) {
    const { transmissionType, flags, cloudFrontSignature } = applicationData;
    const isLive = transmissionType === TRANSMISSION_TYPE.live;

    const src = this.isEncryptedUrl(applicationData) ? asset.urlEncrypted : asset.url;

    if (isUrlFromCloudFront(src) && !isLive && flags?.ENABLE_PLAYER_SIGNED_URL && !flags?.ENABLE_PLAYER_V4_API) {
      return signCloudfrontUrl(src, cloudFrontSignature?.cookiesVideo);
    }

    return src;
  }

  isOverrideNative() {
    const videoJS = videojs as unknown as IVideoJSPlayer;
    return this.config.html5.vhs.overrideNative || !videoJS.Vhs.supportsNativeHls;
  }

  isEncryptedUrl(applicationData: IApplicationData) {
    const { mediaAssets, isDrmEnabled, transmissionType, flags } = applicationData;
    const isLive = transmissionType === TRANSMISSION_TYPE.live;

    if (!flags?.ENABLE_PLAYER_V4_ENCRYPTION || !this.isOverrideNative()) {
      return false;
    }

    return !isLive && !isDrmEnabled && isUrlFromCloudFront(mediaAssets[0].url);
  }

  async getDRMSource(sources: videojs.Tech.SourceObject[], applicationData: IApplicationData) {
    const keySystem = await DRMService.selectKeySystem();

    const source: IDRMSource = this.getSourceType(sources, keySystem) || sources[0];
    const licenseURL = pallyconLicenseEndpoint(applicationData);
    source.keySystems = DRMService.getKeySystems(licenseURL, applicationData.clubMembershipId);

    return source;
  }

  getSourceType(sources: videojs.Tech.SourceObject[], mediaKey: string) {
    if (mediaKey === DRM_PROVIDERS.WIDEVINE || mediaKey === DRM_PROVIDERS.PLAYREADY) {
      return sources.find((source) => source.src.includes(EXTENSION_TYPES.DASH));
    }

    return sources.find((source) => source.src.includes(EXTENSION_TYPES.HLS));
  }

  getPlugins(applicationData: IApplicationData) {
    return {
      mux: MuxService.getConfig(applicationData),
    };
  }
}

export default new PlayerConfigService();
